import { FormControlLabel, Grid, Switch } from "@material-ui/core";
import EmptyPlaceholder from "components/EmptyPlaceholder/EmptyPlaceholder";
import WidgetTitle from "features/landing/components/FleetManagementDashboard/WidgetTitle/WidgetTitle";
import ServiceListDialog from "features/vms/vendorDashboard/ServicesGraphWidget/ServiceListDialog";
import moment from "moment";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import regression from "regression";
import { theme } from "styles/theme";
import { dateFilterTypes } from "../../../../loads/components/LoadsDashboard/loadsDashboardConsts";
import { NewWashDashboardStyle } from "../NewWashDashboardStyle";

const TotalAssetsWidget = ({ washes, isLoading }) => {
    const classes = NewWashDashboardStyle();

    const { activityFilters } = useSelector(state => state.washDashboardState);

    const [showUniqueAssets, setShowUniqueAssets] = useState(false);

    const isByDay = activityFilters['dateFilterType'].value < dateFilterTypes.QUARTER;
    const isByMonth = activityFilters['dateFilterType'].value == dateFilterTypes.MONTH;
    const key = isByDay ? 'day' : 'month';
    const format = isByDay ? "YYYY-MM-DD" : "YYYY-MM";

    const groupedData = useMemo(() => {
        if (!washes || washes.length === 0) return [];

        const start = moment(activityFilters['startDate'].value).startOf(key);
        const end = moment(activityFilters['endDate']?.value).startOf(key);
        let keys = [];
        let regressionKeys = new Set();
        if (isByMonth) {
            let current = start.clone();
            let seen = new Set();
            while (current <= end) {
                const week = current.isoWeek();
                const year = current.isoWeekYear();
                const weekKey = `${year}-W${week}`;
                if (!seen.has(weekKey)) {
                    keys.push(weekKey);
                    seen.add(weekKey);
                    regressionKeys.add(weekKey);
                }
                current = current.add(1, 'week');
            }
        } else {
            let currentKey = start.clone();
            while (currentKey <= end) {
                const currentKeyStr = currentKey.format(format);
                keys.push(currentKeyStr);
                currentKey = currentKey.add(1, key);
                //Only calculate regression if this key is in the past
                if (currentKey <= moment().startOf('day')) {
                    regressionKeys.add(currentKeyStr);
                }
            }
        }

        const grouped = {};
        washes.forEach(wash => {
            let groupKey = wash.groupKey;
            if (isByMonth) {
                const week = moment(groupKey).isoWeek();
                const year = moment(groupKey).isoWeekYear();
                groupKey = `${year}-W${week}`;
            }
            if (!grouped[groupKey]) {
                grouped[groupKey] = showUniqueAssets ? new Set() : 0;
            }
            if (showUniqueAssets) {
                if (wash.vehicleId) grouped[groupKey].add(wash.vehicleId);
            } else {
                grouped[groupKey] += 1;
            }
        });

        const data = keys.map((k, idx) => ({
            name: isByMonth ? `week ${parseInt(k.split('-W')[1], 10)}` : isByDay ? moment(k).format("DD/MM") : moment(k).format("MMM"),
            groupKey: k,
            count: showUniqueAssets
                ? (grouped[k] ? grouped[k].size : 0)
                : (grouped[k] || 0),
            month: k,
            day: k,
            includeInRegression: regressionKeys.has(k)
        }));

        // Calculate regression
        const dataForRegression = data.filter(d => d.includeInRegression).map((d, idx) => [idx, d.count]);

        //const points = data.map((point, index) => [index, point.count]);
        const result = regression.linear(dataForRegression);
        const regressionStats = {
            slope: result.equation[0],
            intercept: result.equation[1],
            r2: result.r2
        };

        // Add trend line points
        return data.map((point, index) => ({
            ...point,
            trend: result.predict(index)[1],
            slope: regressionStats.slope,
            r2: regressionStats.r2
        }));
    }, [washes, activityFilters, key, format, isByDay, isByMonth, showUniqueAssets]);

    const CustomTooltip = ({ label, payload }) => {
        if (!payload || !payload.length || isLoading) return null;
        const data = payload[0];
        const trendValue = payload[1]?.value;

        return (
            <div className={classes.graphTooltipCustom}>
                <div className={classes.graphTooltipLabel}>{label}</div>
                {data.payload.slope !== undefined && (
                    <div className={classes.graphTooltipTrendWrapper}>
                        <div className={classes.graphTooltipItem}>
                            {`Trend: ${trendValue.toFixed(1)}, Slope: ${data.payload.slope > 0 ? '+' : ''}${data.payload.slope.toFixed(2)}, R² = ${(data.payload.r2 * 100).toFixed(1)}%`}
                        </div>
                    </div>
                )}
                <div className={classes.graphTooltipItemsWrapper}>
                    <div className={classes.graphTooltipItem} style={{ color: data.color }}>
                        {`Total: ${data.value}`}
                    </div>
                </div>
            </div>
        );
    };

    const mapExportData = (a => {
        return {
            ['Time Period']: a.groupKey,
            ['Total']: a.count,
        };
    });

    return <Grid container direction="column">
        <Grid container direction='row'>
            <Grid item xs={11}>
                <WidgetTitle
                    title='Total Assets Washed (Aggregate)'
                    loading={isLoading}
                    mapExportData={mapExportData}
                    dataToExport={groupedData}
                    filters={
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={showUniqueAssets}
                                        onChange={e => setShowUniqueAssets(e.target.checked)}
                                        color="primary"
                                    />
                                }
                                label="Show Unique Assets"
                            />
                        </Grid>
                    }
                />
            </Grid>
            <Grid item xs={1} className={classes.exceptionTableIconWrapper}>
                <ServiceListDialog activities={washes} title={'Assets Washed'} />
            </Grid>
        </Grid>

        <Grid item>
            {!!groupedData?.length ? <ResponsiveContainer width="100%" height={300}>
                <LineChart
                    data={groupedData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
                >
                    <XAxis
                        axisLine={{ stroke: '#ccc' }}
                        tickLine={false}
                        dataKey="name"
                        interval={0}
                        tick={{ fontSize: 10 }}
                    />
                    <YAxis
                        axisLine={{ stroke: '#ccc' }}
                        width={30}
                        tickLine={false}
                        tick={{ color: theme.palette.primary.main, fontSize: '0.7em', fontWeight: 'bold' }}
                        domain={[0, 'auto']}
                        tickCount={6}
                        allowDecimals={false}
                    />
                    <Tooltip content={CustomTooltip} />
                    <Line
                        type="monotone"
                        dataKey="count"
                        stroke={theme.palette.primary.main}
                        fillOpacity={0.3}
                        dot={false}
                    />
                    <Line
                        type="monotone"
                        dataKey="trend"
                        stroke={theme.palette.primary.main}
                        strokeDasharray="5 5"
                        dot={false}
                        legendType="none"
                    />
                </LineChart>
            </ResponsiveContainer> :
                <EmptyPlaceholder text='No Assets Washed' />}
        </Grid>
    </Grid>
}
export default TotalAssetsWidget;